import React, { useEffect, useState } from "react"
import axios from "axios"
import { useAuth } from "../contexts/AuthContext"

const API = process.env.REACT_APP_API_URL || "http://localhost:3001/api"

const GRUPOS_PERMISSOES = [
  { id:"monitoramento", label:"🗺 Monitoramento", itens:[
    {id:"mapa_vivo",          label:"Mapa ao vivo"},
    {id:"mapa_popup",         label:"Popup com dados"},
    {id:"mapa_replay",        label:"Replay de trajeto"},
    {id:"mapa_exportar",      label:"Exportar posições"},
    {id:"video_monitoramento", label:"🎥 Vídeo Monitoramento (só o superadmin libera)"},
  ]},
  { id:"ia", label:"🤖 Inteligência Artificial", itens:[
    {id:"ia_usar", label:"Usar o assistente de IA (tem custo: marque só quem precisa)"},
  ]},
  { id:"viagens", label:"🧳 Viagens e Despesas", itens:[
    {id:"viagens_lancar", label:"Motorista: lançar despesas nas viagens em que é o responsável (app)"},
    {id:"viagens_ver",    label:"Consulta: ver todas as viagens e despesas da empresa"},
    {id:"viagens_editar", label:"Gestor: criar viagens, aprovar/rejeitar despesas e encerrar"},
  ]},
  { id:"frota", label:"🚗 Gestão de Frota", itens:[
    {id:"ativos_ver",         label:"Visualizar ativos"},
    {id:"ativos_editar",      label:"Cadastrar/editar ativos"},
    {id:"ativos_desinstalar", label:"Desinstalar ativo"},
    {id:"ativos_desinstalados",label:"Ativos desinstalados"},
    {id:"rastreadores_ver",   label:"Visualizar rastreadores"},
    {id:"rastreadores_editar",label:"Cadastrar/editar rastreadores"},
  ]},
  { id:"cadastros", label:"👥 Cadastros", itens:[
    {id:"clientes_ver",       label:"Visualizar clientes"},
    {id:"clientes_editar",    label:"Cadastrar/editar clientes"},
    {id:"motoristas_ver",     label:"Visualizar motoristas"},
    {id:"motoristas_editar",  label:"Cadastrar/editar motoristas"},
    {id:"grupos_ver",         label:"Visualizar grupos"},
    {id:"grupos_editar",      label:"Cadastrar/editar grupos"},
    {id:"usuarios_ver",       label:"Visualizar usuários"},
    {id:"usuarios_editar",    label:"Cadastrar/editar usuários"},
  ]},
  { id:"manutencao", label:"🔧 Manutenção", itens:[
    {id:"manutencao_ver",     label:"Visualizar manutenções"},
    {id:"manutencao_editar",  label:"Cadastrar/editar manutenções"},
    {id:"manutencao_concluir",label:"Concluir manutenção"},
    {id:"alertas_ver",        label:"Visualizar alertas"},
  ]},
  { id:"relatorios", label:"📊 Relatórios", itens:[
    {id:"rel_frota",          label:"Relatório de Frota"},
    {id:"rel_manutencoes",    label:"Relatório de Manutenções"},
    {id:"rel_alertas",        label:"Relatório de Alertas"},
    {id:"rel_hora_motor",     label:"Hora Motor"},
    {id:"rel_trajeto",        label:"Relatório de Trajeto"},
    {id:"rel_agendamentos",   label:"Agendamento de relatórios"},
  ]},
  { id:"financeiro", label:"💰 Financeiro", itens:[
    {id:"contratos_ver",      label:"Visualizar contratos"},
    {id:"contratos_editar",   label:"Cadastrar/editar contratos"},
    {id:"planos_ver",         label:"Visualizar planos"},
    {id:"planos_editar",      label:"Cadastrar/editar planos"},
    {id:"fechamento_ver",     label:"Fechamento mensal"},
  ]},
  { id:"lancamentos", label:"🧾 Lançamentos e veículos do cliente", itens:[
    {id:"lancamento_manual", label:"Lançamentos manuais: lançar abastecimento e despesas em nome do motorista (não aprova). Só o administrador concede"},
    {id:"ativos_simples",    label:"Veículos (tela simples): trocar o ID do veículo e incluir vencimentos de documentos e seguro. Só o administrador concede"},
  ]},
  { id:"pneus", label:"🛞 Controle de pneus", itens:[
    {id:"pneus_ver",    label:"Consultar pneus, diagramas e relatórios (sem valores). Só o administrador concede"},
    {id:"pneus_lancar", label:"Lançar: montar, desmontar, rodízio e medir sulco/calibragem (inclui consultar). Só o administrador concede"},
    {id:"pneus_gerir",  label:"Gerir: cadastrar pneus, recapar, dar baixa, modelos de eixos, limites e ver custos (inclui tudo). Só o administrador concede"},
  ]},
  { id:"administracao", label:"⚙️ Administração", itens:[
    {id:"whatsapp",           label:"WhatsApp"},
    {id:"configuracoes",      label:"Configurações do sistema"},
  ]},
]

const TODOS_IDS = GRUPOS_PERMISSOES.flatMap(g=>g.itens.map(i=>i.id))
// itens 100% MoobiFleet (cadastro tecnico, contratos/planos, WhatsApp, sistema): o cliente nem ve na lista; so o superadmin
const PERMISSOES_SO_MOOBI = ["ativos_editar", "ativos_desinstalar", "ativos_desinstalados", "rastreadores_ver", "rastreadores_editar", "clientes_ver", "clientes_editar",
  "contratos_ver", "contratos_editar", "planos_ver", "planos_editar", "fechamento_ver", "whatsapp", "configuracoes"]
const PERMISSOES_PLATAFORMA = ["video_monitoramento"]  // so o superadmin concede; fora do padrao dos perfis
const PERMISSOES_SO_ADMIN = ["lancamento_manual", "ativos_simples", "pneus_ver", "pneus_lancar", "pneus_gerir"]  // so o administrador (ou superadmin) concede: nao aparecem para quem nao e administrador
const PERMISSOES_OPCIONAIS = ["ia_usar", "viagens_lancar", "viagens_ver", "viagens_editar", "lancamento_manual", "ativos_simples", "pneus_ver", "pneus_lancar", "pneus_gerir"]  // opcionais: nao entram no padrao dos perfis nem no botao "Todas" (a IA tem custo)

const PERFIS_PADRAO:Record<string,string[]> = {
  superadmin: TODOS_IDS,
  admin:      TODOS_IDS.filter(i=>!PERMISSOES_SO_MOOBI.includes(i) && !PERMISSOES_PLATAFORMA.includes(i) && !PERMISSOES_OPCIONAIS.includes(i)),
  operador:   ["mapa_vivo","mapa_popup","mapa_replay","ativos_ver","rastreadores_ver",
               "motoristas_ver","manutencao_ver","manutencao_editar","manutencao_concluir",
               "alertas_ver","rel_frota","rel_manutencoes","rel_hora_motor"],
  visualizador:["mapa_vivo","mapa_popup","ativos_ver"],
}

const PERFIS = [
  {id:"superadmin",  label:"Super Admin",   cor:"#f87171"},
  {id:"admin",       label:"Administrador", cor:"#f59e0b"},
  {id:"operador",    label:"Operador",      cor:"#0ea5e9"},
  {id:"visualizador",label:"Visualizador",  cor:"#64748b"},
]

const FORM_VAZIO = {
  cliente_id:"", nome:"", email:"", telefone:"", perfil:"operador", permissoes:[] as string[]
}

export default function Usuarios() {
  const { usuario: usuarioLogado, acessarComoUsuario } = useAuth()
  const ehSuper = usuarioLogado?.perfil === "superadmin"
  // permissoes que so o superadmin concede nao aparecem (nem entram no "Todas") pra quem nao e superadmin
  const ehAdminOuSuper = ehSuper || usuarioLogado?.perfil === "admin"
  const GRUPOS_VISIVEIS = ehSuper ? GRUPOS_PERMISSOES : GRUPOS_PERMISSOES.map(g=>({...g, itens: g.itens.filter(i=>!PERMISSOES_PLATAFORMA.includes(i.id) && !PERMISSOES_SO_MOOBI.includes(i.id) && (ehAdminOuSuper || !PERMISSOES_SO_ADMIN.includes(i.id)))})).filter(g=>g.itens.length>0)
  const IDS_VISIVEIS = GRUPOS_VISIVEIS.flatMap(g=>g.itens.map(i=>i.id))
  const IDS_TODAS = IDS_VISIVEIS.filter(i=>!PERMISSOES_OPCIONAIS.includes(i))  // "Todas" nao liga o que e opcional
  const [dados, setDados]       = useState<any[]>([])
  const [clientes, setClientes] = useState<any[]>([])
  const [modal, setModal]       = useState(false)
  const [modalPerm, setModalPerm] = useState<any>(null)
  const [editando, setEditando] = useState<any>(null)
  const [form, setForm]         = useState<any>(FORM_VAZIO)
  const [busca, setBusca]       = useState("")
  const [abaAtiva, setAbaAtiva] = useState<"ativo"|"inativo">("ativo")
  const [loading, setLoading]   = useState(true)
  const [salvando, setSalvando] = useState(false)

  const carregar = async () => {
    try {
      const [u,c] = await Promise.all([
        axios.get(`${API}/usuarios`),
        axios.get(`${API}/clientes`)
      ])
      setDados(u.data); setClientes(c.data)
    } catch {} finally { setLoading(false) }
  }

  useEffect(()=>{ carregar() },[])

  const abrirNovo = () => { setEditando(null); setForm(FORM_VAZIO); setModal(true) }

  const abrirEditar = (u:any) => {
    setEditando(u)
    setForm({
      cliente_id:u.cliente_id||"", nome:u.nome||"", email:u.email||"",
      telefone:u.telefone||"", perfil:u.perfil||"operador",
      permissoes: u.permissoes?.itens || PERFIS_PADRAO[u.perfil||"operador"] || []
    })
    setModal(true)
  }

  const selecionarPerfil = (perfil:string) => {
    setForm({...form, perfil, permissoes: PERFIS_PADRAO[perfil]||[]})
  }

  const togglePerm = (id:string, perms:string[], setter:any) => {
    if (perms.includes(id)) setter(perms.filter((p:string)=>p!==id))
    else setter([...perms, id])
  }

  const toggleGrupo = (grupoId:string, perms:string[], setter:any) => {
    const grupo = GRUPOS_VISIVEIS.find(g=>g.id===grupoId)
    if (!grupo) return
    const ids = grupo.itens.map(i=>i.id)
    const todosAtivos = ids.every(id=>perms.includes(id))
    if (todosAtivos) setter(perms.filter(p=>!ids.includes(p)))
    else { const s = new Set([...perms, ...ids]); setter(Array.from(s)) }
  }

  const salvar = async () => {
    if (!form.cliente_id) return alert("Selecione um cliente")
    if (!form.nome) return alert("Informe o nome completo")
    if (!form.email) return alert("Informe o e-mail")
    setSalvando(true)
    try {
      const payload = { ...form, permissoes: { itens: form.permissoes } }
      if (editando) await axios.put(`${API}/usuarios/${editando.id}`, payload)
      else {
        await axios.post(`${API}/usuarios`, payload)
        alert("✅ Usuário criado! Senha enviada por e-mail.")
      }
      setModal(false); carregar()
    } catch(e:any) { alert(e.response?.data?.erro||"Erro ao salvar") }
    finally { setSalvando(false) }
  }

  const reenviarSenha = async (u:any) => {
    if (!window.confirm(`Reenviar senha para ${u.email}?\nIsso vai gerar uma nova senha e invalidar a atual.`)) return
    try {
      await axios.post(`${API}/usuarios/${u.id}/reenviar-senha`)
      alert("✅ Nova senha enviada para " + u.email)
    } catch { alert("Erro ao reenviar senha") }
  }

  const salvarPermissoes = async () => {
    if (!modalPerm) return
    try {
      await axios.put(`${API}/usuarios/${modalPerm.id}`, {
        permissoes: { itens: modalPerm.permissoes }
      })
      setModalPerm(null); carregar()
    } catch { alert("Erro ao salvar permissões") }
  }

  const filtrados = dados.filter(d => {
    const matchBusca = !busca || d.nome?.toLowerCase().includes(busca.toLowerCase()) || d.email?.toLowerCase().includes(busca.toLowerCase())
    const matchAba = abaAtiva === "ativo" ? d.ativo : !d.ativo
    return matchBusca && matchAba
  })

  const perfilInfo = (p:string) => PERFIS.find(x=>x.id===p)||PERFIS[2]

  const BlocoPermissoes = ({perms, setPerms}: {perms:string[], setPerms:(p:string[])=>void}) => (
    <div>
      {GRUPOS_VISIVEIS.map(grupo=>{
        const ids = grupo.itens.map(i=>i.id)
        const todosAtivos = ids.every(id=>perms.includes(id))
        const algunsAtivos = ids.some(id=>perms.includes(id)) && !todosAtivos
        return (
          <div key={grupo.id} style={{marginBottom:12,border:"1px solid #2d4166",borderRadius:8,overflow:"hidden"}}>
            {/* Header do grupo */}
            <div onClick={()=>toggleGrupo(grupo.id, perms, setPerms)}
              style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                padding:"8px 12px",cursor:"pointer",
                background:todosAtivos?"#0c2a4a":algunsAtivos?"#0d1b2e":"transparent"}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{width:16,height:16,borderRadius:4,flexShrink:0,
                  background:todosAtivos?"#0ea5e9":algunsAtivos?"#0ea5e950":"transparent",
                  border:`2px solid ${todosAtivos||algunsAtivos?"#0ea5e9":"#475569"}`,
                  display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {todosAtivos && <span style={{fontSize:9,color:"white"}}>✓</span>}
                  {algunsAtivos && <span style={{fontSize:10,color:"#0ea5e9",fontWeight:900}}>−</span>}
                </div>
                <span style={{fontSize:12,fontWeight:700,color:todosAtivos?"#f1f5f9":"#94a3b8"}}>{grupo.label}</span>
              </div>
              <span style={{fontSize:10,color:"#475569"}}>
                {ids.filter(id=>perms.includes(id)).length}/{ids.length}
              </span>
            </div>
            {/* Itens do grupo */}
            <div style={{padding:"6px 12px 8px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:4}}>
              {grupo.itens.map(item=>{
                const ativo = perms.includes(item.id)
                return (
                  <div key={item.id} onClick={()=>togglePerm(item.id, perms, setPerms)}
                    style={{display:"flex",alignItems:"center",gap:6,padding:"5px 8px",
                      borderRadius:6,cursor:"pointer",
                      background:ativo?"#0c2a4a20":"transparent",
                      border:`1px solid ${ativo?"#0ea5e9":"transparent"}`}}>
                    <div style={{width:13,height:13,borderRadius:3,flexShrink:0,
                      background:ativo?"#0ea5e9":"transparent",
                      border:`2px solid ${ativo?"#0ea5e9":"#475569"}`,
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {ativo && <span style={{fontSize:8,color:"white"}}>✓</span>}
                    </div>
                    <span style={{fontSize:11,color:ativo?"#e2e8f0":"#64748b"}}>{item.label}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div style={{padding:24,height:"100%",overflowY:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
        <div>
          <h2 style={{fontSize:20,fontWeight:700}}>👥 Usuários</h2>
          <p style={{fontSize:13,color:"#64748b"}}>{filtrados.length} usuário(s)</p>
        </div>
        <button className="btn btn-primary" onClick={abrirNovo}>+ Novo usuário</button>
      </div>

      {/* Abas */}
      <div style={{display:"flex",gap:0,marginBottom:16,borderBottom:"1px solid #2d4166"}}>
        {([["ativo","✅ Ativos"],["inativo","🚫 Inativos"]] as const).map(([id,label])=>{
          const count = dados.filter(d=>id==="ativo"?d.ativo:!d.ativo).length
          return (
            <button key={id} onClick={()=>setAbaAtiva(id)}
              style={{padding:"8px 20px",border:"none",background:"transparent",cursor:"pointer",
                fontFamily:"Inter",fontSize:13,fontWeight:abaAtiva===id?700:400,
                color:abaAtiva===id?"#0ea5e9":"#64748b",
                borderBottom:abaAtiva===id?"2px solid #0ea5e9":"2px solid transparent",
                marginBottom:-1}}>
              {label} <span style={{fontSize:11,color:"#475569",marginLeft:4}}>({count})</span>
            </button>
          )
        })}
      </div>

      <div style={{marginBottom:16}}>
        <input placeholder="Buscar por nome ou e-mail..." value={busca}
          onChange={e=>setBusca(e.target.value)} style={{maxWidth:400}} />
      </div>

      <div className="card" style={{padding:0,overflow:"hidden"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{background:"#0d1b2e"}}>
              {["Nome","E-mail","Cliente","Perfil","Último acesso","Status","Ações"].map(h=>(
                <th key={h} style={{padding:"10px 14px",textAlign:"left",fontSize:11,
                  color:"#64748b",fontWeight:600,textTransform:"uppercase"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{padding:24,textAlign:"center",color:"#64748b"}}>Carregando...</td></tr>
            ) : filtrados.length===0 ? (
              <tr><td colSpan={7} style={{padding:24,textAlign:"center",color:"#64748b"}}>Nenhum usuário</td></tr>
            ) : filtrados.map((u:any)=>{
              const pf = perfilInfo(u.perfil)
              return (
                <tr key={u.id} style={{borderBottom:"1px solid rgba(45,65,102,0.4)"}}>
                  <td style={{padding:"10px 14px"}}>
                    <div style={{fontWeight:600,fontSize:13}}>{u.nome}</div>
                    {u.telefone && <div style={{fontSize:10,color:"#64748b"}}>{u.telefone}</div>}
                    {u.primeiro_acesso && (
                      <span style={{fontSize:9,background:"#451a03",border:"1px solid #f59e0b",
                        borderRadius:8,padding:"1px 6px",color:"#fbbf24",display:"inline-block",marginTop:2}}>
                        Aguardando 1º login
                      </span>
                    )}
                  </td>
                  <td style={{padding:"10px 14px",fontSize:12}}>{u.email}</td>
                  <td style={{padding:"10px 14px",fontSize:12,color:"#94a3b8"}}>{u.cliente_nome||"—"}</td>
                  <td style={{padding:"10px 14px"}}>
                    <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,fontWeight:600,
                      background:`${pf.cor}20`,color:pf.cor,border:`1px solid ${pf.cor}40`}}>
                      {pf.label}
                    </span>
                  </td>
                  <td style={{padding:"10px 14px",fontSize:11,color:"#64748b"}}>
                    {u.ultimo_acesso ? new Date(u.ultimo_acesso).toLocaleString("pt-BR") : "Nunca"}
                  </td>
                  <td style={{padding:"10px 14px"}}>
                    <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,
                      background:u.ativo?"#14532d":"#1e293b",color:u.ativo?"#4ade80":"#64748b"}}>
                      {u.ativo?"Ativo":"Inativo"}
                    </span>
                  </td>
                  <td style={{padding:"10px 14px"}}>
                    <div style={{display:"flex",gap:4}}>
                      {usuarioLogado?.perfil === "superadmin" && u.perfil !== "superadmin" && u.ativo && u.id !== usuarioLogado.id && (
                        <button className="btn btn-ghost" style={{padding:"4px 8px",fontSize:11,color:"#38bdf8"}}
                          title="Acessar como este usuário (ver o que o cliente vê)"
                          onClick={async()=>{
                            if(!window.confirm(`Entrar no sistema como ${u.nome}${u.cliente_nome?` (${u.cliente_nome})`:""}?\n\nO acesso fica registrado na Auditoria e expira em 4 horas.`)) return
                            try { await acessarComoUsuario(u.id) }
                            catch(e:any){ alert(e.response?.data?.erro || "Não foi possível acessar como este usuário") }
                          }}>👁️</button>
                      )}
                      <button className="btn btn-ghost" style={{padding:"4px 8px",fontSize:11}}
                        title="Editar" onClick={()=>abrirEditar(u)}>✏️</button>
                      <button className="btn btn-ghost" style={{padding:"4px 8px",fontSize:11}}
                        title="Permissões"
                        onClick={()=>setModalPerm({
                          ...u, permissoes: u.permissoes?.itens || PERFIS_PADRAO[u.perfil]||[]
                        })}>🔐</button>
                      <button className="btn btn-ghost" style={{padding:"4px 8px",fontSize:11}}
                        title="Reenviar senha" onClick={()=>reenviarSenha(u)}>📧</button>
                      {u.ativo ? (
                        <button className="btn btn-ghost" style={{padding:"4px 8px",fontSize:11,color:"#f87171"}}
                          title="Desativar"
                          onClick={async()=>{ if(window.confirm("Desativar usuário?")) { await axios.put(`${API}/usuarios/${u.id}`,{ativo:false}); carregar() }}}>
                          🚫
                        </button>
                      ) : (
                        <button className="btn btn-ghost" style={{padding:"4px 8px",fontSize:11,color:"#4ade80"}}
                          title="Reativar usuário"
                          onClick={async()=>{ if(window.confirm("Reativar usuário?")) { await axios.put(`${API}/usuarios/${u.id}`,{ativo:true}); carregar() }}}>
                          ✅
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Modal Novo/Editar */}
      {modal && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:1000,
          display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div className="card" style={{width:"100%",maxWidth:640,maxHeight:"92vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:20}}>
              <h3>{editando?"✏️ Editar usuário":"👤 Novo usuário"}</h3>
              <button className="btn btn-ghost" style={{padding:"4px 8px"}} onClick={()=>setModal(false)}>✕</button>
            </div>

            <div className="form-grid">
              <div className="form-group full">
                <label>Cliente *</label>
                <select value={form.cliente_id} onChange={e=>setForm({...form,cliente_id:e.target.value})}>
                  <option value="">— Selecione —</option>
                  {clientes.map((c:any)=><option key={c.id} value={c.id}>{c.razao_social}</option>)}
                </select>
              </div>
              <div className="form-group full">
                <label>Nome completo *</label>
                <input value={form.nome} onChange={e=>setForm({...form,nome:e.target.value})}
                  placeholder="Nome completo" />
              </div>
              <div className="form-group">
                <label>E-mail (login) *</label>
                <input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}
                  placeholder="email@empresa.com" />
                {!editando && <div style={{fontSize:10,color:"#475569",marginTop:3}}>
                  Senha gerada automaticamente e enviada por e-mail
                </div>}
              </div>
              <div className="form-group">
                <label>Telefone</label>
                <input value={form.telefone} onChange={e=>setForm({...form,telefone:e.target.value})}
                  placeholder="(00) 00000-0000" />
              </div>

              <div className="form-group full">
                <label>Perfil base *</label>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:8}}>
                  {PERFIS.map(p=>(
                    <div key={p.id} onClick={()=>selecionarPerfil(p.id)}
                      style={{padding:"8px 12px",borderRadius:8,cursor:"pointer",
                        border:`2px solid ${form.perfil===p.id?p.cor:"#2d4166"}`,
                        background:form.perfil===p.id?`${p.cor}15`:"transparent"}}>
                      <div style={{fontWeight:600,fontSize:12,color:form.perfil===p.id?p.cor:"#f1f5f9"}}>{p.label}</div>
                      <div style={{fontSize:10,color:"#64748b",marginTop:1}}>
                        {PERFIS_PADRAO[p.id]?.length||0} permissões padrão
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{fontSize:10,color:"#475569"}}>
                  Selecionar o perfil pré-configura as permissões abaixo. Você pode ajustar individualmente.
                </div>
              </div>

              <div className="form-group full">
                <label>🔐 Permissões detalhadas</label>
                <div style={{display:"flex",gap:8,marginBottom:8}}>
                  <button type="button" className="btn btn-ghost" style={{fontSize:11}}
                    onClick={()=>setForm({...form,permissoes:[...IDS_TODAS, ...(form.permissoes||[]).filter((p:string)=>PERMISSOES_OPCIONAIS.includes(p))]})}>✅ Todas</button>
                  <button type="button" className="btn btn-ghost" style={{fontSize:11}}
                    onClick={()=>setForm({...form,permissoes:[]})}>❌ Nenhuma</button>
                </div>
                <BlocoPermissoes
                  perms={form.permissoes}
                  setPerms={(p)=>setForm({...form,permissoes:p})} />
              </div>
            </div>

            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:20}}>
              <button className="btn btn-ghost" onClick={()=>setModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando}>
                {salvando?"⏳...":editando?"💾 Salvar":"✅ Criar usuário"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Permissões */}
      {modalPerm && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:1000,
          display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div className="card" style={{width:"100%",maxWidth:560,maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:12}}>
              <h3>🔐 Permissões — {modalPerm.nome}</h3>
              <button className="btn btn-ghost" style={{padding:"4px 8px"}} onClick={()=>setModalPerm(null)}>✕</button>
            </div>

            <div style={{display:"flex",gap:8,marginBottom:12,flexWrap:"wrap"}}>
              <button className="btn btn-ghost" style={{fontSize:11}}
                onClick={()=>setModalPerm({...modalPerm,permissoes:[...IDS_TODAS, ...(modalPerm.permissoes||[]).filter((p:string)=>PERMISSOES_OPCIONAIS.includes(p))]})}>✅ Todas</button>
              <button className="btn btn-ghost" style={{fontSize:11}}
                onClick={()=>setModalPerm({...modalPerm,permissoes:[]})}>❌ Nenhuma</button>
              <button className="btn btn-ghost" style={{fontSize:11}}
                onClick={()=>setModalPerm({...modalPerm,permissoes:PERFIS_PADRAO[modalPerm.perfil]||[]})}>
                🔄 Padrão do perfil
              </button>
              <span style={{fontSize:10,color:"#64748b",alignSelf:"center"}}>
                {modalPerm.permissoes?.length||0}/{IDS_VISIVEIS.length} permissões ativas
              </span>
            </div>

            <BlocoPermissoes
              perms={modalPerm.permissoes||[]}
              setPerms={(p)=>setModalPerm({...modalPerm,permissoes:p})} />

            <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
              <button className="btn btn-ghost" onClick={()=>setModalPerm(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={salvarPermissoes}>💾 Salvar permissões</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
